import type { TooltipTwoColumnsRow } from '$components/tooltip';
import { AccountGroup } from '$lib/accountGroup';
import { categoryToAccountGroups } from '$lib/codegen/audit_account_groups';
import type { AuditContext } from '$lib/context';
import { logError } from '$lib/log_error';
import type { CommonFetcher } from '$lib/services/api';
import { sortByString } from '$lib/sort';
import type { BalanceExpectations } from '$types/analytical_procedures.type';
import type { MaterialityBenchmarks } from '$types/materiality.type';
import type { RiskHandlingMetadata } from '$types/risk_handling.type';
import {
  bookYearLedgerDecoder,
  voucherIdMapDecoder,
  type AccountBalanceWithNumber,
  type AccountInfo,
  type AccountingPeriod,
  type AccountReference,
  type AllLedgerData,
  type BalanceDifference,
  type BookYearLedger,
  type Entry,
  type EventObject,
  type GroupBalance,
  type GroupBalances,
  type GroupCategoryAmounts,
  type MergedAccountBalance,
  type MultiYearAccountData,
  type MultiYearAccountDifferences,
  type MultiYearOverviewData,
  type OpeningBalancesAccounts,
  type OpeningBalancesData,
  type PeriodicLedgerData,
  type SortingLedgerVouchersByKey,
  type Voucher,
  type VoucherIdMap,
} from '$types/sie.type';
import { VoucherEntryId } from '$types/schemas/voucher_entry_id';
import { createWorkerStore } from '$workers/index';
import type { LedgerDataRequest, LedgerDataResponse } from '$workers/types';
import {
  Amount,
  type CaseId,
  definedNotNull,
  ObjectMap,
  Percent,
  Time,
  VoucherId,
} from '@df/common';
import { err, ok } from 'neverthrow';
import { get, writable } from 'svelte/store';
import { caseStore } from './case';
import { notifications, type ProcessResult } from './notifications';

const currentBookYearString = writable<Time.BookYear | undefined>(undefined);
const bookYearFilter = writable<Time.BookYear | undefined>(undefined);
const showEmptyGroups = writable(false);
const remappedAccounts = writable<Map<AccountReference, AccountGroup>>(new Map());
const activeMonths = writable<Set<string> | undefined>(new Set());
const voucherIdMap = writable<VoucherIdMap>(new Map());

function createSieStore() {
  const store = writable<BookYearLedger | undefined>();

  function populate(data: BookYearLedger) {
    const caseBookYearString = get(caseStore)?.bookYear;
    if (definedNotNull(caseBookYearString)) {
      currentBookYearString.set(caseBookYearString);
      bookYearFilter.set(caseBookYearString);
    }

    store.set(data);
  }

  function reset() {
    siePromise.set(undefined);
    store.set(undefined);
    const caseBookYearString = get(caseStore)?.bookYear;
    if (definedNotNull(caseBookYearString)) {
      currentBookYearString.set(caseBookYearString);
      bookYearFilter.set(caseBookYearString);
    }
    remappedAccounts.set(new Map());
    activeMonths.set(new Set());
  }

  async function resetWithFetch(context: AuditContext) {
    try {
      siePromise.set(undefined);
      store.set(undefined);
      const caseBookYearString = context.case.bookYear;
      currentBookYearString.set(caseBookYearString);
      bookYearFilter.set(caseBookYearString);
      remappedAccounts.set(new Map());
      activeMonths.set(new Set());

      await fetchSieStore(context.case.caseId, context.commonFetcher);

      return ok(undefined);
    } catch (error) {
      return err({ type: 'Unknown', error });
    }
  }

  function getDataByBookYear(bookYear?: Time.BookYear): AllLedgerData | undefined {
    if (bookYear) {
      return get(store)?.get(bookYear);
    } else {
      const caseInfo = get(caseStore);
      if (caseInfo?.bookYear) {
        return get(store)?.get(caseInfo?.bookYear);
      }
    }
  }

  function getBookYears(): Time.BookYear[] | undefined {
    const sie = get(store);
    if (definedNotNull(sie)) {
      return [...sie.keys()].sort(Time.BookYear.compare);
    }
  }

  function getFilterBookYears(current?: Time.BookYear):
    | {
        current: Time.BookYear;
        previous?: Time.BookYear;
        next?: Time.BookYear;
      }
    | undefined {
    if (!definedNotNull(current)) return;

    let previous: Time.BookYear | undefined = undefined;
    let next: Time.BookYear | undefined = undefined;

    const bookYears = getBookYears();
    const currentIndex = bookYears?.findIndex((bookYear) => bookYear === current);

    if (!definedNotNull(currentIndex)) return;
    if (currentIndex !== 0) {
      previous = bookYears?.at(currentIndex - 1);
    }
    next = bookYears?.at(currentIndex + 1);

    return { current, previous, next };
  }

  function getAccountInfo(
    accountNumber: string,
    bookYear?: Time.BookYear,
  ): AccountInfo | undefined {
    const allLedgerData = getDataByBookYear(bookYear);

    return allLedgerData?.ledger.accounts.get(accountNumber);
  }

  function getAllUsedAccounts(filter?: string): string[] | undefined {
    const ledger = getDataByBookYear();

    if (definedNotNull(ledger)) {
      return [...ledger.ledger.accounts].reduce((previous, [account]) => {
        let shouldShow = false;
        if (isAccountRelevant(account)) {
          shouldShow = true;

          if (definedNotNull(filter) && filter.length > 0) {
            if (!account.startsWith(filter)) {
              shouldShow = false;
            }
          }
        }

        if (shouldShow) {
          previous.push(account);
        }

        return previous;
      }, [] as string[]);
    }
  }

  function isAccountRelevant(accountNumber: string, bookYear?: Time.BookYear): boolean {
    const ledger = getDataByBookYear(bookYear);

    const accountInfo = getAccountInfo(accountNumber, bookYear);

    return (
      (accountInfo?.numOccurrences ?? 0) > 0 ||
      !definedNotNull(accountInfo) ||
      !Amount.isZero(accountInfo.closingBalance) ||
      (ledger?.accountVouchers.has(accountNumber) ?? false)
    );
  }

  function getGroupAccounts(): ObjectMap<AccountGroup, string[]> {
    const ledger = getDataByBookYear();
    const previousBookYear = findPreviousBookYear();

    const activeGroups = new ObjectMap<AccountGroup, string[]>();

    if (definedNotNull(ledger)) {
      for (const [group, accounts] of ledger.groupAccounts) {
        let activeAccounts = new Set<string>();
        const hasActiveAccounts = accounts.some((number) => isAccountRelevant(number));
        if (hasActiveAccounts) {
          activeAccounts = new Set([...activeAccounts, ...accounts]);
        }

        if (!group.isBalanceGroup() && definedNotNull(previousBookYear)) {
          const previousLedger = getDataByBookYear(previousBookYear);
          const accounts = previousLedger?.groupAccounts.get(group) ?? [];
          const hasActiveAccounts = accounts.some((number) =>
            isAccountRelevant(number, previousBookYear),
          );

          if (hasActiveAccounts) {
            activeAccounts = new Set([...activeAccounts, ...accounts]);
          }
        }

        if (activeAccounts.size > 0) {
          activeGroups.set(group, [...activeAccounts]);
        }
      }
    }

    return activeGroups;
  }

  function getGroupCategoryAmounts(
    groupAccounts: ObjectMap<AccountGroup, string[]>,
  ): GroupCategoryAmounts {
    const groupCategoryAmounts: GroupCategoryAmounts = new Map();

    for (const [category, groups] of categoryToAccountGroups) {
      if (!groupCategoryAmounts.has(category)) {
        let total = Amount.zero();
        const newGroups = new ObjectMap<AccountGroup, string[]>();

        if (category === 'AfterFinancialNet') {
          total = groupCategoryAmounts.get('OperatingProfit')?.total ?? Amount.zero();
        }

        if (category === 'Result') {
          total = groupCategoryAmounts.get('AfterFinancialNet')?.total ?? Amount.zero();
        }

        for (const groupId of groups) {
          const accountGroup = AccountGroup.fromGroupId(groupId);
          const accounts = groupAccounts.get(accountGroup);
          if (definedNotNull(accounts)) {
            newGroups.set(accountGroup, accounts);
          }
          total = Amount.add(total, sie.getGroupTotal(accountGroup) ?? Amount.zero());
        }

        groupCategoryAmounts.set(category, { groups: newGroups, total });
      }
    }
    return groupCategoryAmounts;
  }

  type GroupAccounts = ObjectMap<AccountGroup, AccountReference[]>;
  function getAccountGroupSections(
    showEmptyGroups: boolean,
  ): [GroupAccounts, GroupAccounts] | undefined {
    const ledger = getDataByBookYear();
    if (!definedNotNull(ledger)) {
      return undefined;
    }

    const groupAccounts = [...ledger.groupAccounts.entries()].map(
      ([group, accounts]) =>
        [
          group,
          accounts.filter((account) => showEmptyGroups || isAccountRelevant(account)),
        ] satisfies [AccountGroup, AccountReference[]],
    );

    const balance: GroupAccounts = new ObjectMap();
    const result: GroupAccounts = new ObjectMap();

    for (const [group, accounts] of groupAccounts) {
      if (!showEmptyGroups && accounts.length === 0) {
        continue;
      }
      const currentSection = group.isBalanceGroup() ? balance : result;
      currentSection.set(group, accounts);
    }

    return [balance, result];
  }

  function optimisticallyUpdateGroupAccounts(newAccount: AccountReference, newGroup: AccountGroup) {
    const groups = getDataByBookYear()?.groupAccounts;
    const oldGroup = [...(groups ?? [])].find(([, accounts]) =>
      accounts.some((account) => account === newAccount),
    )?.[0];
    if (definedNotNull(groups) && definedNotNull(oldGroup)) {
      const newOldAccounts = groups.get(newGroup);
      const oldOldAccounts = groups.get(oldGroup);
      if (definedNotNull(newOldAccounts) && definedNotNull(oldOldAccounts)) {
        // Hack to get rid of duplicates
        const newAccounts = [...new Set([...newOldAccounts, newAccount])];
        const oldAccounts = [
          ...new Set(oldOldAccounts.filter((account) => account !== newAccount)),
        ];

        groups.set(newGroup, newAccounts);
        groups.set(oldGroup, oldAccounts);

        updateGroupTotal(newGroup, newAccounts);
        updateGroupTotal(oldGroup, oldAccounts);
      }
    }

    store.update((value) => {
      const caseInfo = get(caseStore);
      if (caseInfo?.bookYear) {
        const bookYearString = caseInfo.bookYear;

        if (definedNotNull(value) && definedNotNull(groups)) {
          const oldData = value?.get(bookYearString);
          if (definedNotNull(oldData)) {
            value.set(bookYearString, { ...oldData, groupAccounts: groups });
          }
        }
      }
      return value;
    });
  }

  let draggedAccounts: AccountReference[];

  function updateAfterDragAndDrop({ event, group }: EventObject) {
    const groups = getDataByBookYear()?.groupAccounts;

    if (event.detail.info.trigger === 'droppedIntoZone') {
      const oldAccounts = groups?.get(group);

      // Get and set the accounts for the groupId dragged into
      draggedAccounts = event.detail.items.map((item) => {
        return item.number;
      });

      if (definedNotNull(oldAccounts)) {
        // Hack to get rid of duplicates
        const newAccounts = [...new Set([...oldAccounts, ...draggedAccounts])];

        groups?.set(group, newAccounts);

        updateGroupTotal(group, newAccounts);
      }
    } else {
      const oldAccounts = groups?.get(group);

      const accountsLeft = oldAccounts?.filter((account) => !draggedAccounts.includes(account));

      if (definedNotNull(accountsLeft)) {
        groups?.set(group, accountsLeft);

        updateGroupTotal(group, accountsLeft);
      }
    }
  }

  function getGroupTotal(group: AccountGroup, bookYear?: Time.BookYear): Amount | undefined {
    const ledger = getDataByBookYear(bookYear);

    return ledger?.closingBalances.groupTotals.get(group);
  }

  function updateGroupTotal(group: AccountGroup, newAccounts: AccountReference[]) {
    // Reset group total to add up again
    const groupTotals = getDataByBookYear()?.closingBalances.groupTotals;
    groupTotals?.set(group, Amount.zero());
    newAccounts.forEach((account) => {
      const currentTotal = groupTotals?.get(group);
      const accountInfo = sie.getAccountInfo(account);

      if (definedNotNull(currentTotal) && definedNotNull(accountInfo)) {
        groupTotals?.set(group, Amount.add(currentTotal, accountInfo.closingBalance));
        const caseInfo = get(caseStore);

        const data = getDataByBookYear();
        data?.closingBalances.groupTotals.set(
          group,
          Amount.add(currentTotal, accountInfo.closingBalance),
        );
        // Need to use update here so subscribers get notified
        store.update((value) => {
          if (caseInfo?.bookYear) {
            const bookYearString = caseInfo?.bookYear;
            if (definedNotNull(value) && definedNotNull(data)) {
              value.set(bookYearString, data);
            }
          }

          return value;
        });
      }
    });
  }

  function getAccountingPeriods(bookYear: Time.BookYear): AccountingPeriod[] {
    const ledger = getDataByBookYear(bookYear);
    return ledger?.periodicData.financialOverview.periods ?? [];
  }

  function getActivePeriods(bookYear: Time.BookYear) {
    const active = new Set<string>();
    const ledger = getDataByBookYear(bookYear);
    if (definedNotNull(ledger)) {
      if (ledger.periodicData.type === 'YearMonth') {
        for (const balances of ledger.periodicData.financialOverview.groupBalances.values()) {
          for (const key of [...balances.periodChange.keys()]) {
            active.add(Time.YearMonth.isYearMonth(key) ? key : Time.BookYear.display(key));
          }
        }
      } else {
        active.add(bookYear);
      }
    }
    return active;
  }

  function getFinancialOverviewData(
    bookYear: Time.BookYear,
  ): [AccountingPeriod[] | undefined, GroupBalances] {
    const ledger = getDataByBookYear(bookYear);

    const groupBalances: ObjectMap<AccountGroup, GroupBalance> = new ObjectMap();

    if (definedNotNull(ledger)) {
      for (const group of [...ledger.periodicData.financialOverview.groupBalances.keys()]) {
        const accounts = ledger.groupAccounts.get(group);
        if (accounts?.some((number) => isAccountRelevant(number, bookYear))) {
          const value = ledger.periodicData.financialOverview.groupBalances.get(group);
          if (value) {
            groupBalances.set(group, value);
          }
        }
      }

      return [ledger.periodicData.financialOverview.periods, groupBalances];
    }

    return [undefined, groupBalances];
  }

  function getRelevantAccountGroups(bookYear: Time.BookYear) {
    const ledger = getDataByBookYear(bookYear);
    const groups: AccountGroup[] = [];
    const groupAccounts = ledger?.groupAccounts ?? new Map<AccountGroup, AccountReference[]>();

    for (const [group, accounts] of groupAccounts) {
      const someAccountUsed = accounts.some((account) => isAccountRelevant(account, bookYear));
      if (someAccountUsed) {
        groups.push(group);
      }
    }

    return groups;
  }

  function calculateInterval(
    data: {
      expected: Amount;
      deviation: Percent;
    },
    latestBalance: Amount | undefined,
  ): { title: TooltipTwoColumnsRow[]; insideInterval: boolean } {
    let insideInterval = false;
    const title: TooltipTwoColumnsRow[] = [
      ['Utgående balans', definedNotNull(latestBalance) ? Amount.display(latestBalance) : '-'],
    ];

    const expectedValue = data.expected;
    const upperPercent = Percent.fromNumber(100 + data.deviation);
    const lowerPercent = Percent.fromNumber(100 - data.deviation);
    const lower = Amount.multiply(data.expected, lowerPercent);
    const upper = Amount.multiply(data.expected, upperPercent);

    if (definedNotNull(latestBalance)) {
      if (Amount.isDebit(latestBalance)) {
        insideInterval = latestBalance >= lower && latestBalance <= upper;
        title.push(['Förväntat belopp', `${Amount.display(lower)} - ${Amount.display(upper)}`]);
      } else {
        insideInterval = latestBalance >= upper && latestBalance <= lower;
        title.push(['Förväntat belopp', `${Amount.display(upper)}  –  ${Amount.display(lower)}`]);
      }
    } else {
      title.push(['Förväntat belopp', Amount.display(expectedValue)]);
    }

    return { title, insideInterval };
  }

  function getMultiYearOverviewData(
    currentBookYear: Time.BookYear | undefined,
    expectations: {
      data: BalanceExpectations | null | undefined;
      filterGroup: boolean;
      filterAccount: boolean;
      materialGroups?: AccountGroup[];
    },
  ): MultiYearOverviewData {
    const bookYears =
      getBookYears()
        ?.reverse()
        .filter(
          (bookYear) =>
            !currentBookYear || Time.BookYear.last(currentBookYear) >= Time.BookYear.last(bookYear),
        ) ?? [];
    const balances = new Map<Time.BookYear, GroupBalances>();
    const allGroups: string[] = [];
    const firstBookYear = bookYears.at(0);

    for (const bookYear of bookYears) {
      const usedGroups = getRelevantAccountGroups(bookYear);
      const groupBalances: GroupBalances = new ObjectMap();
      for (const group of usedGroups) {
        if (
          expectations.materialGroups &&
          !expectations.materialGroups.some((materialGroup) => materialGroup.equals(group))
        ) {
          continue;
        }
        const expectation = expectations?.data?.groupBalances.get(group.stringId());
        const firstLedgerData = getDataByBookYear(firstBookYear);
        const ledgerData = getDataByBookYear(bookYear);
        const latestBalance =
          firstLedgerData?.periodicData.financialOverview.groupBalances.get(group)?.closingBalance;
        const _balances = ledgerData?.periodicData.financialOverview.groupBalances.get(group);

        if (_balances) {
          if (expectations?.filterGroup && definedNotNull(expectation)) {
            const interval = calculateInterval(expectation, latestBalance);
            if (!interval.insideInterval) {
              groupBalances.set(group, _balances);
              allGroups.push(group.stringId());
            }
          } else {
            groupBalances.set(group, _balances);
            allGroups.push(group.stringId());
          }
        }
      }

      balances.set(bookYear, groupBalances);
    }

    const uniqueGroups = new Set([...allGroups]);
    const sortedGroups = new Set(
      AccountGroup.sortAccountGroups([...uniqueGroups].map(AccountGroup.fromGroupId)),
    );

    const groups = new ObjectMap<AccountGroup, MultiYearAccountData>();

    for (const group of sortedGroups) {
      const data = getMultiYearAccountData(group, bookYears, {
        data: expectations.data,
        filterAccount: expectations.filterAccount,
      });

      groups.set(group, data);
    }

    return {
      bookYears,
      balances,
      groups,
    };
  }

  function getMultiYearAccountData(
    group: AccountGroup,
    bookYears: Time.BookYear[],
    expectations?: {
      data: BalanceExpectations | null | undefined;
      filterAccount: boolean;
    },
  ): MultiYearAccountData {
    const firstBookYear = bookYears.at(0);
    const balances = new Map<Time.BookYear, Map<AccountReference, AccountBalanceWithNumber>>();
    let accounts = new Map<AccountReference, string>();

    for (const bookYear of bookYears) {
      const accountsData = new Map(
        getSelectedAccountsFromGroup(group, bookYear).map((account) => [account.number, account]),
      );
      balances.set(bookYear, accountsData);

      for (const [reference, { name }] of accountsData) {
        const expectation = expectations?.data?.accountBalances.get(reference);
        if (expectations?.filterAccount && definedNotNull(expectation) && firstBookYear) {
          const latestBalance = new Map(
            getSelectedAccountsFromGroup(group, firstBookYear).map((account) => [
              account.number,
              account,
            ]),
          ).get(reference)?.closingBalance;
          const interval = calculateInterval(expectation, latestBalance);
          if (!interval.insideInterval) {
            accounts.set(reference, name);
          }
        } else {
          accounts.set(reference, name);
        }
      }
    }

    accounts = new Map([...accounts.entries()].sort(([a], [b]) => sortByString(a, b)));

    const differences = calculateDifferencesForAccounts(
      group,
      [...accounts.keys()],
      balances,
      bookYears,
    );

    return {
      balances,
      accounts,
      differences,
    };
  }

  /**
   * Checks the difference between opening balance in one year and closing balance in the following year
   */
  function getBalanceDifference(
    isBalance: boolean,
    currentYear: Time.BookYear,
    currentYearClosingBalance: Amount | undefined,
    nextYearOpeningBalance: Amount | undefined,
  ): BalanceDifference | undefined {
    const isLastYear = currentYear === get(caseStore)?.bookYear;

    if (!(!isLastYear && isBalance && definedNotNull(currentYearClosingBalance))) return;

    const isEqual = currentYearClosingBalance === (nextYearOpeningBalance ?? Amount.zero());

    if (isEqual) {
      return { type: 'Valid', title: Amount.display(currentYearClosingBalance) };
    } else {
      return {
        type: 'Error',
        title:
          `UB: ${Amount.display(currentYearClosingBalance)}\n` +
          `IB:   ${definedNotNull(nextYearOpeningBalance) ? Amount.display(nextYearOpeningBalance) : '-'}\n` +
          `Diff: ${Amount.displayAbsolute(Amount.subtract(currentYearClosingBalance, nextYearOpeningBalance ?? Amount.zero()))}`,
      };
    }
  }

  /**
   * Calculates the difference for given book years
   */
  function calculateDifferencesForAccounts(
    group: AccountGroup,
    accounts: string[],
    balances: Map<Time.BookYear, Map<AccountReference, AccountBalanceWithNumber>>,
    bookYears: Time.BookYear[],
  ): MultiYearAccountDifferences {
    const differences: MultiYearAccountDifferences = new Map();
    for (const [index, bookYear] of bookYears.entries()) {
      const nextBookYear = index !== 0 ? bookYears.at(index - 1) : undefined;
      const accountDifferences = new Map<AccountReference, BalanceDifference>();
      for (const account of accounts) {
        const difference = getBalanceDifference(
          group.isBalanceGroup(),
          bookYear,
          balances.get(bookYear)?.get(account)?.closingBalance,
          definedNotNull(nextBookYear)
            ? balances.get(nextBookYear)?.get(account)?.openingBalance
            : undefined,
        );

        if (definedNotNull(difference)) {
          accountDifferences.set(account, difference);
        }
      }

      differences.set(bookYear, accountDifferences);
    }

    return differences;
  }

  function getSelectedAccountsFromGroup(
    group: AccountGroup,
    bookYear: Time.BookYear,
  ): AccountBalanceWithNumber[] {
    const ledger = getDataByBookYear(bookYear);
    const accounts = ledger?.groupAccounts.get(group);

    if (!definedNotNull(accounts)) {
      return [];
    }

    return accounts.reduce((prev: AccountBalanceWithNumber[], account) => {
      const ledgerAccount = ledger?.ledger.accounts.get(account);
      const selectedAccount = ledger?.periodicData.financialOverview.accountBalances.get(account);
      if (
        definedNotNull(ledgerAccount) &&
        isAccountRelevant(account, bookYear) &&
        definedNotNull(selectedAccount)
      ) {
        prev.push({ ...selectedAccount, number: account, name: ledgerAccount.name });
      }

      return prev;
    }, []);
  }

  function mergeAccounts(
    accounts: AccountBalanceWithNumber[] | undefined,
    previousYearAccounts: AccountBalanceWithNumber[] | undefined,
    isBalance: boolean,
  ): MergedAccountBalance[] {
    const allAccounts = new Set<string>();
    for (const account of [
      ...(accounts ?? []),
      ...(!isBalance ? (previousYearAccounts ?? []) : []),
    ]) {
      allAccounts.add(account.number);
    }

    const merged: MergedAccountBalance[] = [];
    for (const account of allAccounts) {
      const current = accounts?.find((a) => a.number === account);
      const previous = previousYearAccounts?.find((a) => a.number === account);

      if (isBalance) {
        merged.push({
          openingBalance: current?.openingBalance ?? null,
          closingBalance: current?.closingBalance ?? null,
          periodChange: current?.periodChange ?? new ObjectMap(),
          periodClosingBalance: current?.periodClosingBalance ?? new ObjectMap(),
          number: current?.number ?? previous?.number ?? 'Nummer saknas',
          name: current?.name ?? previous?.name ?? 'Namn saknas',
        });
      } else {
        merged.push({
          openingBalance: previous?.closingBalance ?? null,
          closingBalance: current?.closingBalance ?? null,
          periodChange: current?.periodChange ?? new ObjectMap(),
          periodClosingBalance: current?.periodClosingBalance ?? new ObjectMap(),
          number: current?.number ?? previous?.number ?? 'Nummer saknas',
          name: current?.name ?? previous?.name ?? 'Namn saknas',
        });
      }
    }

    return merged.sort((a, b) => sortByString(a.number, b.number));
  }

  function getClosingBalances(bookYear: Time.BookYear): {
    groups: ObjectMap<AccountGroup, Amount>;
    accounts: Map<AccountReference, Amount>;
  } {
    const ledger = getDataByBookYear(bookYear);
    const groups = new ObjectMap<AccountGroup, Amount>();
    const accounts = new Map<AccountReference, Amount>();

    const usedGroups = getRelevantAccountGroups(bookYear);
    for (const group of usedGroups) {
      const groupBalance = ledger?.closingBalances.groupTotals.get(group);
      if (definedNotNull(groupBalance)) {
        groups.set(group, groupBalance);
      }

      const groupAccounts =
        ledger?.groupAccounts
          .get(group)
          ?.filter((account) => isAccountRelevant(account, bookYear)) ?? [];

      for (const account of groupAccounts) {
        const balance = ledger?.closingBalances.closingBalance.get(account);

        if (definedNotNull(balance)) {
          accounts.set(account, balance);
        }
      }
    }

    return {
      groups,
      accounts,
    };
  }

  function getVouchers(
    account: string,
    options?: { bookYear?: Time.BookYear; period?: Time.YearMonth },
  ): Voucher[] {
    const ledger = getDataByBookYear(options?.bookYear);
    const voucherIds = ledger?.accountVouchers.get(account);

    return [...(voucherIds ?? [])].reduce((vouchers, voucherId) => {
      let voucher: Voucher | undefined = undefined;
      if (definedNotNull(options) && definedNotNull(options.period)) {
        const tempVoucher = ledger?.ledger.vouchers.get(voucherId);
        if (
          definedNotNull(tempVoucher) &&
          Time.YearMonth.monthNumber(options.period) === tempVoucher.date.getMonth() + 1
        ) {
          voucher = tempVoucher;
        }
      } else {
        voucher = ledger?.ledger.vouchers.get(voucherId);
      }

      if (definedNotNull(voucher)) {
        vouchers.push(voucher);
      }

      return vouchers;
    }, [] as Voucher[]);
  }

  function getVoucherId(voucherId: VoucherId, bookYear?: Time.BookYear) {
    const ledger = getDataByBookYear(bookYear);

    return ledger?.ledger.vouchers.get(voucherId);
  }

  function getVoucherByIdReactively(
    voucherId: VoucherId,
    data: BookYearLedger | undefined,
    bookYear?: Time.BookYear | undefined,
  ) {
    if (definedNotNull(data)) {
      const currentBookYear = get(currentBookYearString);

      const ledger = definedNotNull(bookYear)
        ? (data.get(bookYear) ??
          (definedNotNull(currentBookYear) ? data.get(currentBookYear) : undefined))
        : definedNotNull(currentBookYear)
          ? data.get(currentBookYear)
          : undefined;

      return ledger?.ledger.vouchers.get(voucherId);
    }
  }

  function getVoucherEntriesById(voucherId: VoucherId) {
    const voucher = getVoucherId(voucherId);

    return voucher?.entries;
  }

  function getPeriodicData(bookYear: Time.BookYear): PeriodicLedgerData | undefined {
    const ledger = getDataByBookYear(bookYear);

    return ledger?.periodicData;
  }

  function getMaterialityBenchmarks(): MaterialityBenchmarks | undefined {
    const ledger = getDataByBookYear();

    return ledger?.benchmarks;
  }

  // TODO#Cleanup this is unused
  function getGroupSelectorGroups(): [AccountGroup[], AccountGroup[]] {
    const ledger = getDataByBookYear();
    const balanceGroups: AccountGroup[] = [];
    const resultGroups: AccountGroup[] = [];

    if (definedNotNull(ledger)) {
      for (const group of [...ledger.groupAccounts.keys()]) {
        if (group.isBalanceGroup()) {
          balanceGroups.push(group);
        } else {
          resultGroups.push(group);
        }
      }
    }

    return [balanceGroups, resultGroups];
  }

  async function getBalanceAndResultGroups(
    promise: Promise<ProcessResult>,
  ): Promise<AccountGroup[][]> {
    const groups = await promise.then(() => {
      const ledger = getDataByBookYear();
      const balanceGroups: AccountGroup[] = [];
      const resultGroups: AccountGroup[] = [];

      if (definedNotNull(ledger)) {
        for (const group of [...ledger.groupAccounts.keys()]) {
          if (group.isBalanceGroup()) {
            balanceGroups.push(group);
          } else {
            resultGroups.push(group);
          }
        }
      }

      return [balanceGroups, resultGroups];
    });

    return groups;
  }

  async function getBalanceOrResultAccounts(
    type: 'balance' | 'result',
    promise: Promise<ProcessResult>,
    otherBookYear: Time.BookYear | undefined = undefined,
  ): Promise<AccountBalanceWithNumber[]> {
    const [balance, result] = await getBalanceAndResultGroups(promise);

    const accounts: AccountBalanceWithNumber[] = [];

    let bookYear: Time.BookYear | undefined = undefined;
    const currentBookYear = get(currentBookYearString);
    if (definedNotNull(otherBookYear)) {
      bookYear = otherBookYear;
    } else if (definedNotNull(currentBookYear)) {
      bookYear = currentBookYear;
    }

    if (definedNotNull(bookYear)) {
      if (type === 'balance') {
        for (const group of balance) {
          accounts.push(...getSelectedAccountsFromGroup(group, bookYear));
        }
      } else {
        for (const group of result) {
          accounts.push(...getSelectedAccountsFromGroup(group, bookYear));
        }
      }
    }

    return accounts;
  }

  function getAccounts(
    otherBookYear: Time.BookYear | undefined = undefined,
  ): AccountBalanceWithNumber[] {
    const accounts: AccountBalanceWithNumber[] = [];

    let bookYear: Time.BookYear | undefined = undefined;
    const currentBookYear = get(currentBookYearString);
    if (definedNotNull(otherBookYear)) {
      bookYear = otherBookYear;
    } else if (definedNotNull(currentBookYear)) {
      bookYear = currentBookYear;
    }
    const data = getDataByBookYear(bookYear);

    if (definedNotNull(bookYear)) {
      for (const group of data?.groupAccounts.keys() ?? []) {
        accounts.push(...getSelectedAccountsFromGroup(group, bookYear));
      }
    }

    return accounts;
  }

  function getAccountEntryAmount(entries: Entry[], voucherAccount: AccountReference): Amount {
    return Amount.sum(entries, ({ account, amount }) =>
      account === voucherAccount ? amount : undefined,
    );
  }

  function calculateVoucherBalance(
    vouchers: Voucher[],
    accountReference: AccountReference,
    bookYear: Time.BookYear | undefined,
  ): Promise<Map<VoucherId, Amount>> {
    const promise = new Promise<Map<VoucherId, Amount>>((resolve) => {
      const balances = new Map<VoucherId, Amount>();
      let runningBalance = sie.getAccountInfo(accountReference, bookYear)?.openingBalance;
      for (const voucher of [...vouchers].sort((a, b) =>
        sortVoucherData(a, b, { type: 'date', order: 'asc' }, accountReference),
      )) {
        if (definedNotNull(runningBalance)) {
          const entryAmount = getAccountEntryAmount(voucher.entries, accountReference);
          const balance = Amount.add(runningBalance, entryAmount);

          balances.set(voucher.voucherId, balance);
          runningBalance = balance;
        }
      }

      resolve(balances);
    });

    return promise;
  }

  function findPreviousBookYear(): Time.BookYear | undefined {
    const currentBookYear = get(caseStore)?.bookYear;
    const previousBookYearLast = currentBookYear
      ? Time.BookYear.dayBefore(currentBookYear)
      : undefined;
    const data = get(store);
    if (definedNotNull(data) && definedNotNull(previousBookYearLast)) {
      const bookYears = [...data.keys()].map((bookYearString) => bookYearString);
      const previousBookYear = bookYears.find(
        (bookYear) => Time.BookYear.last(bookYear) === previousBookYearLast,
      );
      return previousBookYear;
    }
  }

  function getPreviousBookYearData(): AllLedgerData | undefined {
    const previousBookYear = findPreviousBookYear();

    if (!definedNotNull(previousBookYear)) {
      return undefined;
    }

    return getDataByBookYear(previousBookYear);
  }

  function getGeneralLedgerAccountInfo(account: string): {
    currentYear: AccountInfo | undefined;
    previousYear?: AccountInfo;
  } {
    const currentLedger = getDataByBookYear();
    const previousYearLedger = getPreviousBookYearData();
    const data: {
      currentYear: AccountInfo | undefined;
      previousYear?: AccountInfo;
    } = {
      currentYear: currentLedger?.ledger.accounts.get(account),
    };

    if (definedNotNull(previousYearLedger) && parseInt(account.slice(0, 2)) >= 30) {
      data.previousYear = previousYearLedger.ledger.accounts.get(account);
    }

    return data;
  }

  function getOpeningBalancesData(riskHandlingMetadata: RiskHandlingMetadata):
    | {
        data: OpeningBalancesData[];
        noDifference: boolean;
      }
    | undefined {
    const previousBookYear = getPreviousBookYearData();
    const currentBookYear = getDataByBookYear();
    const groupsToAudit = riskHandlingMetadata.groupsToAudit;

    if (
      !definedNotNull(previousBookYear) ||
      !definedNotNull(currentBookYear) ||
      !definedNotNull(groupsToAudit)
    ) {
      return undefined;
    }

    const previousGroups = [
      ...previousBookYear.periodicData.financialOverview.groupBalances.keys(),
    ].map((group) => group.stringId());
    const currentGroups = [
      ...currentBookYear.periodicData.financialOverview.groupBalances.keys(),
    ].map((group) => group.stringId());

    const groupIds = [...new Set([...previousGroups, ...currentGroups])];
    const groups = groupIds.map((groupId) => AccountGroup.fromGroupId(groupId));
    groups.sort();

    let noDifference = true;

    const data: OpeningBalancesData[] = groups
      .filter((group) => group.isBalanceGroup())
      .filterMap((group) => {
        // Using filter map with the following check because anonymous functions
        // do not know type data from outside its scope
        const closingBalance =
          previousBookYear.periodicData.financialOverview.groupBalances.get(group)?.closingBalance;
        const openingBalance =
          currentBookYear.periodicData.financialOverview.groupBalances.get(group)?.openingBalance;
        if (
          definedNotNull(openingBalance) &&
          (!definedNotNull(closingBalance) || closingBalance !== openingBalance)
        ) {
          noDifference = false;
        }

        const previousYear = sie.findPreviousBookYear();
        const currentYear = get(caseStore)?.bookYear;
        const accounts: OpeningBalancesAccounts = {
          balances: new ObjectMap(),
          data: new Map(),
          differences: new Map(),
        };
        if (definedNotNull(previousYear) && definedNotNull(currentYear)) {
          const data = getMultiYearAccountData(group, [currentYear, previousYear]);
          const differences = data.differences.get(previousYear);

          accounts.balances = data.balances;
          accounts.data = data.accounts;

          if (definedNotNull(differences)) {
            accounts.differences = differences;
          }
        }
        return [
          group,
          {
            closingBalance,
            openingBalance,
            difference: definedNotNull(previousYear)
              ? getBalanceDifference(true, previousYear, closingBalance, openingBalance)
              : undefined,
            accounts,
          },
        ];
      });

    return {
      data,
      noDifference,
    };
  }

  function isEntryIdOnAccount(account: string, entryId: VoucherEntryId) {
    const ledger = getDataByBookYear();
    const voucherId = VoucherEntryId.voucherId(entryId);
    const voucher = ledger?.ledger.vouchers.get(voucherId);

    if (definedNotNull(voucher)) {
      for (const entry of voucher.entries) {
        if (entry.id === entryId && entry.account === account) {
          return true;
        }
      }
    }

    return false;
  }

  // Sorting functions
  function sortVoucherData(
    a: Voucher,
    b: Voucher,
    sorting: SortingLedgerVouchersByKey,
    account: AccountReference,
  ): number {
    const aAmount = getAccountEntryAmount(a.entries, account);
    const bAmount = getAccountEntryAmount(b.entries, account);
    switch (sorting.type) {
      case 'voucher':
        if (sorting.order === 'desc') return sortByString(a.voucherId, b.voucherId);
        if (sorting.order === 'asc') return sortByString(b.voucherId, a.voucherId);
        break;
      case 'text':
        if (sorting.order === 'desc') return sortByString(a.text, b.text);
        if (sorting.order === 'asc') return sortByString(b.text, a.text);
        break;
      case 'amount':
        if (aAmount === bAmount) return 0;
        if (sorting.order === 'desc') return aAmount > bAmount ? -1 : 1;
        if (sorting.order === 'asc') return bAmount > aAmount ? -1 : 1;
        break;
      case 'date':
        if (a.date.getTime() === b.date.getTime()) {
          if (sorting.order === 'desc') {
            return sortByString(b.voucherId, a.voucherId);
          }
          if (sorting.order === 'asc') {
            return sortByString(a.voucherId, b.voucherId);
          }
        }
        if (sorting.order === 'desc') return a.date.getTime() > b.date.getTime() ? -1 : 1;
        if (sorting.order === 'asc') return b.date.getTime() > a.date.getTime() ? -1 : 1;
        break;
      default:
        break;
    }

    return sortByString(a.voucherId, b.voucherId);
  }

  const { subscribe, set, update } = store;

  return {
    subscribe,
    set,
    update,
    populate,
    reset,
    resetWithFetch,
    getDataByBookYear,
    getBookYears,
    getFilterBookYears,
    getAccountInfo,
    getGroupAccounts,
    getGroupCategoryAmounts,
    getClosingBalances,
    getBalanceAndResultGroups,
    getAccountGroupSections,
    optimisticallyUpdateGroupAccounts,
    updateAfterDragAndDrop,
    getGroupTotal,
    getFinancialOverviewData,
    getMultiYearOverviewData,
    getMultiYearAccountData,
    calculateInterval,
    getBalanceDifference,
    getActiveMonths: getActivePeriods,
    getAccountingPeriods,
    getSelectedAccountsFromGroup,
    mergeAccounts,
    getVouchers,
    getVoucherId,
    getVoucherByIdReactively,
    getVoucherEntriesById,
    getPeriodicData,
    getMaterialityBenchmarks,
    getGroupSelectorGroups,
    getBalanceOrResultAccounts,
    getAccounts,
    getAccountEntryAmount,
    calculateVoucherBalance,
    findPreviousBookYear,
    getPreviousBookYearData,
    getGeneralLedgerAccountInfo,
    getOpeningBalancesData,
    isAccountRelevant,
    getAllUsedAccounts,
    isEntryIdOnAccount,
    sortVoucherData,
  };
}

const sie = createSieStore();
const siePromise = writable<Promise<ProcessResult> | undefined>();
const sieResolve = writable<((arg: ProcessResult) => void) | undefined>();
const ledgerWorker = createWorkerStore<LedgerDataRequest, LedgerDataResponse>(({ data }) => {
  if (data.type === 'Success') {
    const ledger = bookYearLedgerDecoder(data.raw);
    const idMap = voucherIdMapDecoder(data.voucherIdMap);
    voucherIdMap.set(idMap);
    sie.populate(ledger);
  } else {
    notifications.error('SIE datan kunde inte hämtas. Försök igen genom att ladda om sidan.');
  }
  const resolve = get(sieResolve);
  if (definedNotNull(resolve)) {
    resolve(ok(undefined));
  }
});

const fetchSieStore = async (caseId: CaseId, fetcher: CommonFetcher) => {
  const { promise, resolve } = notifications.process({ type: 'Case', id: caseId }, 'Ledger');
  siePromise.set(promise);
  sieResolve.set(resolve);

  const headers = await fetcher.getJsonHeaders({ type: 'Stored' });

  if (headers.isErr()) {
    logError(headers.error, "Could not get headers for 'fetchSieStore'");

    throw headers;
  }

  await ledgerWorker.sendMessage({
    caseId,
    headers: {
      'Content-Type': 'application/json',
      Authorization: headers.value['Authorization'],
      [fetcher.getClientAuthKey()]: headers.value['Client'],
    },
  });
};

export {
  activeMonths,
  bookYearFilter,
  currentBookYearString,
  fetchSieStore,
  ledgerWorker,
  remappedAccounts,
  showEmptyGroups,
  sie,
  siePromise,
  voucherIdMap,
};
